/**
 * NotificationPreferences Component
 * Allows users to configure notification settings
 */
import React, { useState, useEffect } from 'react';
import { notificationService } from '../../services/api/notificationService';
import {
  NotificationPreferences as NotificationPreferencesType,
  NotificationType
} from '../../types/notification.types';
import Button from '../UI/button';
import './NotificationPreferences.css';

const notificationTypeLabels: Record<NotificationType, string> = {
  FOLLOW: 'New Followers',
  LIKE: 'Likes on your trips',
  COMMENT: 'Comments on your trips',
  TRIP_INVITE: 'Trip invitations',
  TRIP_SHARED: 'Trip shares',
  TRIP_PUBLISHED: 'Trip publications',
  COLLABORATION_REQUEST: 'Collaboration requests',
  SYSTEM: 'System notifications'
};

export const NotificationPreferences: React.FC = () => {
  const [preferences, setPreferences] =
    useState<NotificationPreferencesType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    setLoading(true);
    setError(null);
    try {
      const prefs = await notificationService.getPreferences();
      setPreferences(prefs.preferences);
    } catch (err) {
      setError('Failed to load preferences');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!preferences) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await notificationService.updatePreferences(preferences);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save preferences');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updatePreferences = (updates: Partial<NotificationPreferencesType>) => {
    setPreferences((prev) => (prev ? { ...prev, ...updates } : null));
  };

  const toggleNotificationType = (type: NotificationType) => {
    if (!preferences) return;

    const currentTypes = preferences.push.types || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];

    updatePreferences({
      push: { ...preferences.push, types: newTypes }
    });
  };

  if (loading) {
    return (
      <div className='notification-preferences-loading'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className='notification-preferences-error'>
        <p>Unable to load notification preferences</p>
        <Button onClick={loadPreferences}>Retry</Button>
      </div>
    );
  }

  return (
    <div className='notification-preferences'>
      <h2 className='notification-preferences-title'>
        Notification Preferences
      </h2>

      {error && (
        <div
          className='alert alert-danger'
          role='alert'
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className='alert alert-success'
          role='alert'
        >
          Preferences saved successfully!
        </div>
      )}

      <div className='notification-preferences-section'>
        <h3 className='notification-preferences-subtitle'>
          Email Notifications
        </h3>

        <div className='form-check form-switch mb-3'>
          <input
            className='form-check-input'
            type='checkbox'
            id='email-enabled'
            checked={preferences.email.enabled}
            onChange={(e) =>
              updatePreferences({
                email: { ...preferences.email, enabled: e.target.checked }
              })
            }
          />
          <label
            className='form-check-label'
            htmlFor='email-enabled'
          >
            Enable email notifications
          </label>
        </div>

        {preferences.email.enabled && (
          <div className='notification-preferences-field'>
            <label
              htmlFor='email-frequency'
              className='form-label'
            >
              Email Frequency
            </label>
            <select
              id='email-frequency'
              className='form-select'
              value={preferences.email.frequency}
              onChange={(e) =>
                updatePreferences({
                  email: {
                    ...preferences.email,
                    frequency: e.target.value as
                      | 'INSTANT'
                      | 'DAILY'
                      | 'WEEKLY'
                      | 'NEVER'
                  }
                })
              }
            >
              <option value='INSTANT'>Instant</option>
              <option value='DAILY'>Daily digest</option>
              <option value='WEEKLY'>Weekly digest</option>
              <option value='NEVER'>Never</option>
            </select>
          </div>
        )}
      </div>

      <div className='notification-preferences-section'>
        <h3 className='notification-preferences-subtitle'>
          Push Notifications
        </h3>

        <div className='form-check form-switch mb-3'>
          <input
            className='form-check-input'
            type='checkbox'
            id='push-enabled'
            checked={preferences.push.enabled}
            onChange={(e) =>
              updatePreferences({
                push: { ...preferences.push, enabled: e.target.checked }
              })
            }
          />
          <label
            className='form-check-label'
            htmlFor='push-enabled'
          >
            Enable push notifications
          </label>
        </div>

        {preferences.push.enabled && (
          <div className='notification-preferences-types'>
            <p className='notification-preferences-types-label'>
              Choose which notifications you want to receive:
            </p>
            {(Object.keys(notificationTypeLabels) as NotificationType[]).map(
              (type) => (
                <div
                  key={type}
                  className='form-check'
                >
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id={`type-${type}`}
                    checked={preferences.push.types?.includes(type) || false}
                    onChange={() => toggleNotificationType(type)}
                  />
                  <label
                    className='form-check-label'
                    htmlFor={`type-${type}`}
                  >
                    {notificationTypeLabels[type]}
                  </label>
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div className='notification-preferences-section'>
        <h3 className='notification-preferences-subtitle'>Quiet Hours</h3>

        <div className='form-check form-switch mb-3'>
          <input
            className='form-check-input'
            type='checkbox'
            id='quiet-enabled'
            checked={preferences.quietHours.enabled}
            onChange={(e) =>
              updatePreferences({
                quietHours: {
                  ...preferences.quietHours,
                  enabled: e.target.checked
                }
              })
            }
          />
          <label
            className='form-check-label'
            htmlFor='quiet-enabled'
          >
            Enable quiet hours (no notifications during this time)
          </label>
        </div>

        {preferences.quietHours.enabled && (
          <div className='notification-preferences-quiet-hours'>
            <div className='notification-preferences-field'>
              <label
                htmlFor='quiet-start'
                className='form-label'
              >
                Start time
              </label>
              <input
                type='time'
                id='quiet-start'
                className='form-control'
                value={preferences.quietHours.start}
                onChange={(e) =>
                  updatePreferences({
                    quietHours: {
                      ...preferences.quietHours,
                      start: e.target.value
                    }
                  })
                }
              />
            </div>
            <div className='notification-preferences-field'>
              <label
                htmlFor='quiet-end'
                className='form-label'
              >
                End time
              </label>
              <input
                type='time'
                id='quiet-end'
                className='form-control'
                value={preferences.quietHours.end}
                onChange={(e) =>
                  updatePreferences({
                    quietHours: {
                      ...preferences.quietHours,
                      end: e.target.value
                    }
                  })
                }
              />
            </div>
          </div>
        )}
      </div>

      <div className='notification-preferences-actions'>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant='primary'
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
};
